import BaseClient from './BaseClient';

class CampaignsClient extends BaseClient {
    /** Campaign operations */
    campaignsGetAll($teamId) {
        const method = 'get';
        const url = `/api/v3/teams/${$teamId}/campaigns`;

        return this.makeCoreApiRequest(url, method);
    }

    campaignGet($teamId, $campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${$teamId}/campaigns/${$campaignId}`;

        return this.makeCoreApiRequest(url, method);
    }

    dependencies($teamId, $campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${$teamId}/campaigns/${$campaignId}/dependencies`;

        return this.makeCoreApiRequest(url, method);
    }

    campaignsAutocomplete($teamId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${$teamId}/campaigns/autocomplete`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    campaignsGetRecords($teamId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${$teamId}/campaigns`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    campaignsCount($teamId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${$teamId}/campaigns/count`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    campaignsCountByStatus($teamId, params) {
        return this.makeCoreApiRequest(`/api/v3/teams/${$teamId}/campaigns/count/status`, 'get', null, params);
    }

    campaignSave($teamId, $campaign) {
        const isNew = typeof $campaign.id === 'undefined';
        const method = isNew ? 'post' : 'put';
        const url = isNew ? `/api/v3/teams/${$teamId}/campaigns` : `/api/v3/teams/${$teamId}/campaigns/${$campaign.id}`;

        return this.makeCoreApiRequest(url, method, $campaign);
    }

    changeStatus(teamId, campaignId, status) {
        const url = `/api/v3/teams/${teamId}/campaigns/${campaignId}/status`;

        return this.makeCoreApiRequest(url, 'patch', {
            status,
        });
    }

    addContactsToCampaign(teamId, campaignId, contactIds) {
        const method = 'post';
        const url = `/api/v3/teams/${teamId}/campaigns/${campaignId}/contacts`;
        const data = { contact_ids: contactIds };

        return this.makeCoreApiRequest(url, method, data);
    }

    campaignsDeleteContacts($teamId, $campaignId, contactIds) {
        const method = 'post';
        const url = `/api/v3/teams/${$teamId}/campaigns/${$campaignId}/contacts/remove`;
        const data = { contact_ids: contactIds };

        return this.makeCoreApiRequest(url, method, data);
    }

    campaignsContactsCount(teamId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/campaigns/${campaignId}/contacts/count`;

        return this.makeCoreApiRequest(url, method);
    }

    campaignsAllContacts(teamId, campaignId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/campaigns/${campaignId}/contacts`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    campaignDelete($teamId, $campaignId) {
        const method = 'delete';
        const url = `/api/v3/teams/${$teamId}/campaigns/${$campaignId}`;

        return this.makeCoreApiRequest(url, method);
    }

    campaignCapacity(teamId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/campaigns/${campaignId}/capacity`;

        return this.makeCoreApiRequest(url, method);
    }

    campaignsDetails(teamId, params, config = {}) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/campaigns/resource-details`;

        return this.makeCoreApiRequest(url, method, null, params, config);
    }

    groupCampaignsDetails(teamId, groupId, params, config = {}) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/resource-details`;

        return this.makeCoreApiRequest(url, method, null, params, config);
    }

    groupCampaignsGetRecords(teamId, groupId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    groupCampaignGet(teamId, groupId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/${campaignId}`;

        return this.makeCoreApiRequest(url, method);
    }

    groupCampaignDelete(teamId, groupId, campaignId) {
        const method = 'delete';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/${campaignId}`;

        return this.makeCoreApiRequest(url, method);
    }

    groupCampaignSave(teamId, groupId, campaign) {
        const isNew = typeof campaign.id === 'undefined';
        const method = isNew ? 'post' : 'put';
        const url = isNew
            ? `/api/v3/teams/${teamId}/groups/${groupId}/campaigns`
            : `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/${campaign.id}`;

        return this.makeCoreApiRequest(url, method, campaign);
    }

    groupCampaignChangeStatus(teamId, groupId, campaignId, status) {
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/${campaignId}/status`;

        return this.makeCoreApiRequest(url, 'patch', {
            status,
        });
    }

    groupCampaignsCount(teamId, groupId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/count`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    groupCampaignsContactsCount(teamId, groupId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/${campaignId}/contacts/count`;

        return this.makeCoreApiRequest(url, method);
    }

    groupCampaignCapacity(teamId, groupId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/${campaignId}/capacity`;

        return this.makeCoreApiRequest(url, method);
    }

    groupCampaignsEstimations(teamId, groupId, campaignIds) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/estimations`;

        return this.makeCoreApiRequest(url, method, null, { campaign_ids: campaignIds });
    }

    getGroupMessageRules(teamId, groupId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/message-rules`;

        return this.makeCoreApiRequest(url, method);
    }

    groupValidateMessageStandards(teamId, groupId, data) {
        const method = 'post';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/validate-message-standards`;

        return this.makeCoreApiRequest(url, method, data);
    }

    groupCampaignsSendingCalendar(teamId, groupId, audienceType, params, config = {}) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/groups/${groupId}/campaigns/sending-calendar/${audienceType}`;

        return this.makeCoreApiRequest(url, method, null, params, config);
    }

    orgUnitCampaignsDetails(teamId, orgUnitId, params, config = {}) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/resource-details`;

        return this.makeCoreApiRequest(url, method, null, params, config);
    }

    orgUnitCampaignsGetRecords(teamId, orgUnitId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    orgUnitCampaignGet(teamId, orgUnitId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/${campaignId}`;

        return this.makeCoreApiRequest(url, method);
    }

    orgUnitCampaignDelete(teamId, orgUnitId, campaignId) {
        const method = 'delete';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/${campaignId}`;

        return this.makeCoreApiRequest(url, method);
    }

    orgUnitCampaignSave(teamId, orgUnitId, campaign) {
        const isNew = typeof campaign.id === 'undefined';
        const method = isNew ? 'post' : 'put';
        const url = isNew
            ? `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns`
            : `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/${campaign.id}`;

        return this.makeCoreApiRequest(url, method, campaign);
    }

    orgUnitCampaignChangeStatus(teamId, orgUnitId, campaignId, status) {
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/${campaignId}/status`;

        return this.makeCoreApiRequest(url, 'patch', {
            status,
        });
    }

    orgUnitCampaignsCount(teamId, orgUnitId, params) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/count`;

        return this.makeCoreApiRequest(url, method, null, params);
    }

    orgUnitCampaignsContactsCount(teamId, orgUnitId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/${campaignId}/contacts/count`;

        return this.makeCoreApiRequest(url, method);
    }

    orgUnitCampaignCapacity(teamId, orgUnitId, campaignId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/${campaignId}/capacity`;

        return this.makeCoreApiRequest(url, method);
    }

    orgUnitCampaignsEstimations(teamId, orgUnitId, campaignIds) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/estimations`;

        return this.makeCoreApiRequest(url, method, null, { campaign_ids: campaignIds });
    }

    getOrgUnitMessageRules(teamId, orgUnitId) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/message-rules`;

        return this.makeCoreApiRequest(url, method);
    }

    orgUnitValidateMessageStandards(teamId, orgUnitId, data) {
        const method = 'post';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/validate-message-standards`;

        return this.makeCoreApiRequest(url, method, data);
    }

    orgUnitCampaignsSendingCalendar(teamId, orgUnitId, audienceType, params, config = {}) {
        const method = 'get';
        const url = `/api/v3/teams/${teamId}/org-units/${orgUnitId}/campaigns/sending-calendar/${audienceType}`;

        return this.makeCoreApiRequest(url, method, null, params, config);
    }
}

export default new CampaignsClient();
